import React, { useState, useEffect, useRef } from 'react';
import { Truck, Package, DollarSign } from 'lucide-react';

const TransportSelector = ({ onSelectionChange, activity }) => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [transportCost, setTransportCost] = useState('');
    const [showCostInput, setShowCostInput] = useState(false);

    // Ref to track if we are initializing from parent
    const isInitializing = useRef(true);

    const transportOptions = [
        {
            id: 'transport',
            value: 'TRANSPORT',
            label: 'TRANSPORT',
            description: 'Profesjonell leveringstjeneste',
            icon: <Truck className="w-5 h-5" />
        },
        {
            id: 'hente-selv',
            value: 'HENTE_SELV',
            label: ' HENTE SELV',
            description: 'Hent selv alternativ',
            icon: <Package className="w-5 h-5" />
        }
    ];

    const handleOptionChange = (optionValue) => {
        setSelectedOptions(prev => {
            const newSelection = prev.includes(optionValue)
                ? prev.filter(option => option !== optionValue)
                : [...prev, optionValue];
            return newSelection;
        });
    };

    useEffect(() => {
        if (activity) {
            const options = [];
            if (activity.isTransportPartner) options.push('TRANSPORT');
            if (activity.isTransportFree) options.push('HENTE_SELV');

            setSelectedOptions(options);
            setTransportCost(activity.transportPartnerPrice != null ? activity.transportPartnerPrice.toString() : '');
        } else {
            setSelectedOptions([]);
            setTransportCost('');
        }
        isInitializing.current = true; // We just initialized from parent
    }, [activity]);

    useEffect(() => {
        const hasTransport = selectedOptions.includes('TRANSPORT');
        const hasHenteSelv = selectedOptions.includes('HENTE_SELV');

        

        setShowCostInput(hasTransport);

        if (!hasTransport) {
            if (isInitializing.current) {
                // Skip clearing cost on initial load
                isInitializing.current = false;
            } else {
                setTransportCost(0);
            }
        } else {
            // Reset flag once valid selections exist
            isInitializing.current = false;
        }
    }, [selectedOptions]);

    useEffect(() => {
        if (onSelectionChange) {
            const cost = transportCost ? parseFloat(transportCost) : 0;
            const isTransportPartner = selectedOptions.includes('TRANSPORT');
            const isTransportFree = selectedOptions.includes('HENTE_SELV');

            onSelectionChange({
                isTransportPartner,
                isTransportFree,
                transportPartnerPrice: isTransportPartner ? cost : 0
            });
        }
    }, [selectedOptions, transportCost, onSelectionChange]);

    const handleCostChange = (e) => {
        const value = e.target.value;
        // Allow only numbers and decimal point
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setTransportCost(value);
        }
    };

    return (
        <div className="mx-auto bg-white rounded-xl p-6 space-y-6 border border-grey-500">
            <div className="text-center">
                <p className="text-gray-600 font-bold">Velg din foretrukne transportmetode</p>
            </div>

            <div className="space-y-4">
                {transportOptions.map((option) => (
                    <div
                        key={option.id}
                        onClick={() => handleOptionChange(option.value)}
                        className={`
              relative flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all duration-200
              ${selectedOptions.includes(option.value)
                                ? 'border-orange-500 bg-orange-50 shadow-md'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }
            `}
                    >
                        <div className="flex items-center h-5">
                            <input
                                type="checkbox"
                                className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                                checked={selectedOptions.includes(option.value)}
                            />
                        </div>
                        <div className="ml-4 flex-1">
                            <div className="flex items-center space-x-3">
                                <div className={`
                  p-2 rounded-full transition-colors duration-200
                  ${selectedOptions.includes(option.value)
                                        ? 'bg-orange-100 text-orange-600'
                                        : 'bg-gray-100 text-gray-600'
                                    }
                `}>
                                    {option.icon}
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-900">
                                        {option.label}
                                    </div>
                                    {option.description && (
                                        <div className="text-sm text-gray-500">
                                            {option.description}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {selectedOptions.includes(option.value) && (
                            <div className="absolute top-2 right-2">
                                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Cost Input Section - Only shows when TRANSPORT is selected */}
            {showCostInput && (
                <div className="p-2 mb-4">
                    <div className="flex items-center space-x-3 mb-2">
                        <div className="p-2 bg-orange-100 rounded-full">
                            <DollarSign className="w-4 h-4 text-orange-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Transportkostnad</h3>
                    </div>

                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">NOK</span>
                        </div>
                        <input
                            type="text"
                            placeholder="0"
                            value={transportCost}
                            onChange={handleCostChange}
                            required
                            className="block w-full pl-12 pr-3 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm font-medium"
                        />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                        Angi transportkostnaden for partnerleveranse
                    </p>
                </div>
            )}

            {/* Show 0 NOK label when HENTE_SELV is selected */}
            {selectedOptions.includes('HENTE_SELV') && (
                <div className="mt-1 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-green-100 rounded-full">
                                <Package className="w-4 h-4 text-green-600" />
                            </div>
                            <span className="text-lg font-semibold text-gray-900">Hent selv kostnad</span>
                        </div>
                        <div className="text-2xl font-bold text-green-600">
                            0 NOK
                        </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                        Ingen kostnad for selvhenting
                    </p>
                </div>
            )}
        </div>
    );
};

export default TransportSelector;
